import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileSignature, Download, Upload, ShieldCheck, History } from "lucide-react";
import { api, downloadFile, currentRole } from "@/lib/api";
import { Pill, fmtDate, errMsg, DARK_DIALOG, MfaField } from "./shared";
import DirectorCertificateCard from "./DirectorCertificateCard";
import SigningGuide from "./SigningGuide";
import VerificationResult from "./VerificationResult";
import DscExpiryBanner from "./DscExpiryBanner";
import DscTestRunCard from "./DscTestRunCard";

export default function AgreementSigning() {
  const [items, setItems] = useState(null);
  const [cert, setCert] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const isSA = currentRole() === "super_admin";
  const load = () => {
    api.get("/admin/agreements").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
    api.get("/admin/dsc/certificate").then((r) => setCert(r.data)).catch(() => setCert({ registered: false }));
  };
  useEffect(() => { load(); }, []);

  const prepare = async (f, newVersion = false) => {
    try {
      const { data } = await api.post(`/admin/agreements/${f.id}/prepare`, null, { params: { new_version: newVersion } });
      toast.success(`Version ${data.version} ready for signing`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const dl = (f, kind) => downloadFile(`/admin/agreements/${f.id}/${kind}.pdf`, `Karmivo_Agreement_${f.code}_${kind === "signed" ? "SIGNED" : "TO_SIGN"}.pdf`).catch(() => toast.error("Download failed"));

  return (
    <div data-testid="agreement-signing-page" className="max-w-6xl">
      <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold">Super Admin · Legal</div>
      <h1 className="font-display text-3xl font-bold mt-1">Agreement Digital Signature</h1>
      <p className="text-sm text-slate-400 mt-1 mb-6">The Director signs franchise agreements on their own computer with the existing USB-token DSC. Karmivo never sees the private key or PIN — it only verifies the signed PDF.</p>
      <DscExpiryBanner showLink={false}/>
      <div className="grid lg:grid-cols-[1fr_1fr] gap-5 mb-6">
        <DirectorCertificateCard cert={cert} isSA={isSA} onChange={load}/>
        <SigningGuide/>
      </div>
      {isSA && <DscTestRunCard certOk={cert?.registered}/>}
      <AgreementTable items={items} isSA={isSA} certOk={cert?.registered} onPrepare={prepare} onDownload={dl} onUpload={setUploadFor} onHistory={setHistoryFor}/>
      {uploadFor && <UploadSignedDialog f={uploadFor} onClose={() => setUploadFor(null)} onDone={load}/>}
      {historyFor && <HistoryDialog f={historyFor} onClose={() => setHistoryFor(null)}/>}
    </div>
  );
}

function AgreementTable({ items, isSA, certOk, onPrepare, onDownload, onUpload, onHistory }) {
  if (!items) return <div className="text-slate-500 text-sm">Loading…</div>;
  if (!items.length) return <div className="admin-card p-10 text-center text-slate-500" data-testid="ag-empty">No franchise agreements yet. Agreements appear here once a franchisee pays the onboarding fee.</div>;
  const btn = "border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 h-8";
  return (
    <div className="admin-card overflow-x-auto">
      <table className="w-full text-sm min-w-[860px]">
        <thead className="bg-slate-800/70 text-slate-400 text-[10px] uppercase tracking-widest">
          <tr><th className="p-3 text-left">Franchise</th><th className="text-left">Version</th><th className="text-left">Signature status</th><th className="text-left">Signed by</th><th className="text-left">Verified</th><th></th></tr>
        </thead>
        <tbody>
          {items.map((f) => {
            const s = f.signing;
            return (
              <tr key={f.id} className="border-t border-slate-800" data-testid={`ag-row-${f.code}`}>
                <td className="p-3"><div className="font-semibold text-slate-200">{f.business_name}</div><div className="font-mono text-[11px] text-orange-300">{f.code}</div></td>
                <td className="font-mono text-xs">{s ? `v${s.version}` : "—"}</td>
                <td>{s?.status === "signed_verified" ? <span className="text-emerald-300 text-xs font-bold flex items-center gap-1" data-testid={`ag-status-${f.code}`}><ShieldCheck size={13}/>Digitally Signed – Verified</span> : <Pill value={s?.status || "not_prepared"} testid={`ag-status-${f.code}`}/>}</td>
                <td className="text-xs text-slate-400">{s?.signature?.subject_cn || "—"}</td>
                <td className="text-xs text-slate-400">{fmtDate(s?.verified_at)}</td>
                <td className="pr-3"><div className="flex gap-1.5 justify-end flex-wrap">
                  {isSA && !s && <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-8" disabled={!certOk} onClick={() => onPrepare(f)} data-testid={`ag-prepare-${f.code}`}><FileSignature size={13} className="mr-1"/>Prepare for signing</Button>}
                  {s && s.status !== "signed_verified" && <Button size="sm" variant="outline" className={btn} onClick={() => onDownload(f, "unsigned")} data-testid={`ag-download-unsigned-${f.code}`}><Download size={13} className="mr-1"/>Download to sign</Button>}
                  {isSA && s && s.status !== "signed_verified" && <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-8" onClick={() => onUpload(f)} data-testid={`ag-upload-${f.code}`}><Upload size={13} className="mr-1"/>Upload signed PDF</Button>}
                  {f.last_signed_version && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => onDownload(f, "signed")} data-testid={`ag-download-signed-${f.code}`}><Download size={13} className="mr-1"/>Signed PDF v{f.last_signed_version}</Button>}
                  {isSA && s?.status === "signed_verified" && <Button size="sm" variant="ghost" className="text-slate-400 h-8" onClick={() => window.confirm("Issue a new agreement version? The current signed version stays in history.") && onPrepare(f, true)} data-testid={`ag-new-version-${f.code}`}>New version</Button>}
                  {s && <Button size="sm" variant="ghost" className="text-slate-400 h-8" onClick={() => onHistory(f)} data-testid={`ag-history-${f.code}`}><History size={13}/></Button>}
                </div></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UploadSignedDialog({ f, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [mfa, setMfa] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const go = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("mfa_code", mfa);
      const { data } = await api.post(`/admin/agreements/${f.id}/upload-signed`, fd);
      setResult(data); onDone();
      data.ok ? toast.success("Signature verified — agreement delivered to franchisee") : toast.error("Verification failed — see details");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DARK_DIALOG} max-w-lg max-h-[90vh] overflow-y-auto`} data-testid="ag-upload-dialog">
        <DialogHeader><DialogTitle>Upload signed agreement · {f.code}</DialogTitle><DialogDescription className="text-slate-400">Upload the PDF exactly as saved by your signing software. It is verified before being marked signed.</DialogDescription></DialogHeader>
        {result ? <VerificationResult r={result}/> : (
          <div className="space-y-3">
            <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} className="block w-full text-sm text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-slate-200" data-testid="ag-upload-file"/>
            <MfaField value={mfa} onChange={setMfa} testid="ag-upload-mfa"/>
            <div className="flex justify-end"><Button className="bg-orange-500 hover:bg-orange-600" disabled={!file || mfa.length !== 6 || busy} onClick={go} data-testid="ag-upload-confirm">{busy ? "Verifying…" : "Verify & save"}</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ f, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/agreements/${f.id}`).then((r) => setD(r.data)); }, [f.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DARK_DIALOG} max-w-2xl max-h-[90vh] overflow-y-auto`} data-testid="ag-history-dialog">
        <DialogHeader><DialogTitle>Signature history · {f.code}</DialogTitle></DialogHeader>
        {!d ? <div className="text-slate-500 text-sm">Loading…</div> : (
          <div className="space-y-4 text-xs">
            {d.versions.map((v) => (
              <div key={v.id} className="rounded-lg border border-slate-800 p-3">
                <div className="flex justify-between"><b className="font-mono">v{v.version}</b><Pill value={v.status}/></div>
                <div className="text-slate-500 mt-1 font-mono break-all">unsigned sha256 {v.unsigned_sha256}</div>
                {v.signed_sha256 && <div className="text-slate-500 font-mono break-all">signed sha256 {v.signed_sha256}</div>}
                {v.checks && <div className="mt-2"><VerificationResult r={{ ok: v.status === "signed_verified", checks: v.checks, signature: v.signature }}/></div>}
                <div className="text-slate-500 mt-2">{(v.attempts || []).length} upload attempt(s)</div>
              </div>
            ))}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Audit trail</div>
              {d.audit.map((a) => <div key={a.id} className="flex gap-3 py-1 border-t border-slate-800"><span className="font-mono text-slate-500">{fmtDate(a.ts)}</span><span className="text-orange-300">{a.action}</span></div>)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
